import { Box, type BoxProps } from '@mantine/core'
import { clsx } from 'clsx'
import { usePathname } from 'next/navigation'
import React, {
  type ReactNode,
  createContext,
  useState,
  useRef,
  useCallback,
  useMemo,
  type PropsWithChildren,
} from 'react'

import { type DrawerOpenOptions } from './DrawerOpenOptions'
import classes from './styles.module.css'

type DrawersState = {
  openDrawer: (Drawer: ReactNode, options?: DrawerOpenOptions) => void
  closeDrawer: () => void
}

type DrawersProviderProps = PropsWithChildren<BoxProps> & {
  app?: 'flecto-saas' | 'flecto-link' | 'flecto-store'
}

export const DrawersContext = createContext<DrawersState>({
  openDrawer: () => null,
  closeDrawer: () => null,
})

export function DrawersProvider({ children, app }: DrawersProviderProps) {
  const portalRef = useRef<HTMLDivElement>(null)
  const [drawer, setDrawer] = useState<ReactNode>(null)
  const pathname = usePathname()
  const isFlectoApp = app === 'flecto-saas'

  const checkEscAndClose = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeDrawer()
    }
  }, [])

  const checkBackdropAndClose = useCallback((e: Event) => {
    if (e.target === portalRef.current) {
      closeDrawer()
    }
  }, [])

  const closeDrawer = useCallback(() => {
    if (portalRef.current) {
      setDrawer(null)
      portalRef.current.classList.remove(classes.drawersPortalVisible)
      portalRef.current.classList.remove(classes.drawersPortalDrawerVisible)
      portalRef.current.removeEventListener('click', checkBackdropAndClose)
      window.removeEventListener('keyup', checkEscAndClose)
    }
  }, [])

  const openDrawer = useCallback(
    (drawer: ReactNode, paramOptions?: DrawerOpenOptions) => {
      const options: DrawerOpenOptions = {
        closeOnBackdropClick: true,
        closeOnEscKey: true,
        ...paramOptions,
      }

      if (portalRef.current) {
        setDrawer(drawer)
        portalRef.current.classList.add(classes.drawersPortalVisible)

        setTimeout(
          () =>
            portalRef.current?.classList.add(
              classes.drawersPortalDrawerVisible
            ),
          200
        )

        if (options.closeOnBackdropClick) {
          portalRef.current.addEventListener('click', checkBackdropAndClose)
        }

        if (options.closeOnEscKey) {
          window.addEventListener('keyup', checkEscAndClose)
        }
      }
    },
    []
  )

  const actions = useMemo(() => ({ closeDrawer, openDrawer }), [])

  const drawersPortalClasses = clsx(classes.drawersPortal, {
    [classes.appSaas]: isFlectoApp,
    [classes.appSaasSettings]:
      isFlectoApp &&
      (pathname.startsWith('/profile') || pathname.startsWith('/user')),
  })

  return (
    <DrawersContext.Provider value={actions}>
      <Box
        ref={portalRef}
        className={drawersPortalClasses}
        data-testid='drawers-portal'
      >
        {drawer}
      </Box>
      {children}
    </DrawersContext.Provider>
  )
}
